import * as fabric from 'fabric'

// Intensita' dell'effetto traslucido: 1 = nessun effetto, valori < 1 aumentano la trasparenza percepita.
export const TRANSLUCENT_FINISH_OPACITY_MULTIPLIER = 0.7
// Toggle che consentono di abilitare/disabilitare separatamente i due effetti.
export const TRANSLUCENT_ENABLE_OPACITY_EFFECT = false
export const TRANSLUCENT_ENABLE_SHADOW_EFFECT = true
// Proprieta' runtime usata per "congelare" l'opacita' originale dell'oggetto.
// In questo modo il finish visivo non altera il valore base persistito.
export const TRANSLUCENT_BASE_OPACITY_PROP = '__translucentBaseOpacity'
// Proprieta' runtime dove conserviamo l'eventuale shadow originale dell'oggetto.
export const TRANSLUCENT_BASE_SHADOW_PROP = '__translucentBaseShadow'
export const TRANSLUCENT_FINISH_SHADOW = {
  color: 'rgba(0,0,0,0.6)',
  blur: 2,
  offsetX: -2,
  offsetY: -2,
}

type FabricLikeObject = {
  customId?: string | number
  opacity?: number
  shadow?: any
  set?: (key: string, value: any) => void
  [key: string]: any
}

type TranslucentBaseStyle = {
  opacity?: number
  shadow?: any
}

type TecnicaWithTranslucentFinish =
  | {
      editor_finitura_traslucida?: boolean
    }
  | null
  | undefined

const toSpecialIdsSet = (specialObjectIds: Array<string | number> = []) =>
  new Set((specialObjectIds || []).map((id) => String(id)))

export const isRenderableUserCanvasObject = (
  obj: any,
  specialObjectIds: Array<string | number> = []
) => {
  if (!obj?.customId) return false

  return !toSpecialIdsSet(specialObjectIds).has(String(obj.customId))
}

export const normalizeShadowValue = (shadow: any) => {
  if (!shadow) return null

  if (shadow instanceof fabric.Shadow) {
    return shadow.toObject()
  }

  if (typeof shadow === 'object') {
    return { ...shadow }
  }

  return null
}

export const getObjectBaseOpacity = (obj: FabricLikeObject | null | undefined) => {
  if (!obj) return 1

  // Se disponibile usiamo la base salvata prima dell'effetto; altrimenti fallback su opacity corrente.
  const storedBaseOpacity = obj[TRANSLUCENT_BASE_OPACITY_PROP]
  if (typeof storedBaseOpacity === 'number') {
    return storedBaseOpacity
  }

  return typeof obj.opacity === 'number' ? obj.opacity : 1
}

export const getObjectBaseShadow = (obj: FabricLikeObject | null | undefined) => {
  if (!obj) return null

  if (obj[TRANSLUCENT_BASE_SHADOW_PROP] !== undefined) {
    return obj[TRANSLUCENT_BASE_SHADOW_PROP]
  }

  return normalizeShadowValue(obj.shadow)
}

export const applyTranslucentFinishToCanvas = (
  canvas: fabric.Canvas | null | undefined,
  enabled: boolean,
  specialObjectIds: Array<string | number> = []
) => {
  if (!canvas) return false

  let hasVisualChanges = false

  canvas.getObjects().forEach((obj: any) => {
    if (!isRenderableUserCanvasObject(obj, specialObjectIds)) return

    // Salviamo la base una sola volta per evitare effetti cumulativi su aggiornamenti successivi.
    if (typeof obj[TRANSLUCENT_BASE_OPACITY_PROP] !== 'number') {
      obj[TRANSLUCENT_BASE_OPACITY_PROP] = typeof obj.opacity === 'number' ? obj.opacity : 1
    }
    if (obj[TRANSLUCENT_BASE_SHADOW_PROP] === undefined) {
      obj[TRANSLUCENT_BASE_SHADOW_PROP] = normalizeShadowValue(obj.shadow)
    }

    const baseOpacity = getObjectBaseOpacity(obj)
    const baseShadow = getObjectBaseShadow(obj)
    // nextOpacity rimane derivata dalla base, cosi' attiva/disattiva del finish e' reversibile.
    const nextOpacity =
      enabled && TRANSLUCENT_ENABLE_OPACITY_EFFECT
        ? Math.max(0, Math.min(1, baseOpacity * TRANSLUCENT_FINISH_OPACITY_MULTIPLIER))
        : baseOpacity
    const nextShadow =
      enabled && TRANSLUCENT_ENABLE_SHADOW_EFFECT ? TRANSLUCENT_FINISH_SHADOW : baseShadow

    if (obj.opacity !== nextOpacity) {
      obj.set('opacity', nextOpacity)
      hasVisualChanges = true
    }

    const currentShadow = normalizeShadowValue(obj.shadow)
    const expectedShadow = normalizeShadowValue(nextShadow)
    if (JSON.stringify(currentShadow) !== JSON.stringify(expectedShadow)) {
      obj.set('shadow', expectedShadow ? new fabric.Shadow(expectedShadow) : null)
      hasVisualChanges = true
    }
  })

  return hasVisualChanges
}

/**
 * Helper runtime per `useFabricEditor`:
 * applica il finish traslucido e forza il render solo quando c'e' una modifica visiva.
 */
export const applyTranslucentFinishToCanvasAndRender = ({
  canvas,
  enabled,
  specialObjectIds = [],
}: {
  canvas: fabric.Canvas | null | undefined
  enabled: boolean
  specialObjectIds?: Array<string | number>
}) => {
  if (!canvas) return false

  const hasVisualChanges = applyTranslucentFinishToCanvas(canvas, enabled, specialObjectIds)
  if (hasVisualChanges) {
    canvas.requestRenderAll()
  }

  return hasVisualChanges
}

/**
 * Esplicita il toggle tecnico usato dall'editor per la sola resa visiva traslucida.
 */
export const isTranslucentFinishEnabledForTecnica = (tecnica?: TecnicaWithTranslucentFinish) =>
  Boolean(tecnica?.editor_finitura_traslucida)

export const collectBaseTranslucentStyleByObjectId = (
  objects: FabricLikeObject[] = [],
  specialObjectIds: Array<string | number> = []
) => {
  const baseStyleByObjectId = new Map<string, TranslucentBaseStyle>()

  objects.forEach((obj: any) => {
    if (!isRenderableUserCanvasObject(obj, specialObjectIds)) return

    baseStyleByObjectId.set(String(obj.customId), {
      opacity: getObjectBaseOpacity(obj),
      shadow: getObjectBaseShadow(obj),
    })
  })

  return baseStyleByObjectId
}

export const restoreSerializedObjectFromBaseTranslucentStyle = (
  serializedObject: any,
  baseStyle?: TranslucentBaseStyle | null
) => {
  if (!serializedObject || typeof serializedObject !== 'object') return serializedObject
  if (!baseStyle) return serializedObject

  let nextObject = { ...serializedObject }
  const { opacity, shadow } = baseStyle

  // Se la base e' 1 rimuoviamo opacity dal JSON per mantenere il payload pulito e retro-compatibile.
  if (TRANSLUCENT_ENABLE_OPACITY_EFFECT && typeof opacity === 'number') {
    if (Math.abs(opacity - 1) < 0.0001) {
      const { opacity: _unusedOpacity, ...rest } = nextObject
      nextObject = rest
    } else {
      // Se un oggetto nasce con opacity propria (<1), la preserviamo in salvataggio.
      nextObject.opacity = opacity
    }
  }

  // Manteniamo in persistenza la shadow originaria dell'oggetto, non quella del finish visuale.
  if (TRANSLUCENT_ENABLE_SHADOW_EFFECT) {
    if (shadow) {
      nextObject.shadow = shadow
    } else {
      const { shadow: _unusedShadow, ...rest } = nextObject
      nextObject = rest
    }
  }

  return nextObject
}
